import React, { useEffect, useState, useMemo } from 'react';
import {
    Loader2,
    RefreshCw,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    ChevronDown,
    ChevronRight,
    Trophy,
    TrendingUp,
    TrendingDown,
    Clock,
    Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import { benchService, type BenchRun, type BenchFact } from '../../services/api';

export const ScoreboardTab: React.FC = () => {
    const [days, setDays] = useState<number>(30);
    const [caseFilter, setCaseFilter] = useState<string>('all');
    const [runs, setRuns] = useState<BenchRun[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [expandedRunId, setExpandedRunId] = useState<number | null>(null);
    const [factsMap, setFactsMap] = useState<Record<number, BenchFact[]>>({});
    const [loadingFacts, setLoadingFacts] = useState<Record<number, boolean>>({});

    const loadRuns = async () => {
        setLoading(true);
        try {
            const data = await benchService.getRuns(days);
            setRuns(data.runs || []);
        } catch (err: any) {
            toast.error('Failed to load benchmark runs: ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRuns();
    }, [days]);

    const toggleExpand = async (runId: number) => {
        if (expandedRunId === runId) {
            setExpandedRunId(null);
            return;
        }
        setExpandedRunId(runId);
        if (!factsMap[runId] && !loadingFacts[runId]) {
            setLoadingFacts((prev) => ({ ...prev, [runId]: true }));
            try {
                const data = await benchService.getFacts(runId);
                setFactsMap((prev) => ({ ...prev, [runId]: data.facts || [] }));
            } catch (err: any) {
                toast.error('Failed to load run facts: ' + (err.message || 'Unknown error'));
            } finally {
                setLoadingFacts((prev) => ({ ...prev, [runId]: false }));
            }
        }
    };

    // All available case labels
    const caseLabels = useMemo(() => {
        const set = new Set<string>();
        runs.forEach((r) => {
            if (r.case_label) set.add(r.case_label);
        });
        return Array.from(set).sort();
    }, [runs]);

    // Filtered runs
    const filteredRuns = useMemo(() => {
        if (caseFilter === 'all') return runs;
        return runs.filter((r) => r.case_label === caseFilter);
    }, [runs, caseFilter]);

    // Map case_label + night to calculate score diffs
    const runDiffs = useMemo(() => {
        // Sort runs chronologically per case to compute diff vs previous completed run
        const byCase: Record<string, BenchRun[]> = {};
        runs.forEach((r) => {
            if (!byCase[r.case_label]) byCase[r.case_label] = [];
            byCase[r.case_label].push(r);
        });

        const diffMap: Record<number, number | null> = {};
        Object.values(byCase).forEach((caseList) => {
            // Sort ascending by started_at / id
            const sorted = [...caseList].sort(
                (a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
            );
            let prevCompletedScore: number | null = null;
            sorted.forEach((run) => {
                if (run.completed && run.readiness_score !== null && run.readiness_score !== undefined) {
                    if (prevCompletedScore !== null) {
                        diffMap[run.id] = run.readiness_score - prevCompletedScore;
                    } else {
                        diffMap[run.id] = null;
                    }
                    prevCompletedScore = run.readiness_score;
                } else {
                    diffMap[run.id] = null;
                }
            });
        });
        return diffMap;
    }, [runs]);

    // Stats rollup
    const completedCount = useMemo(() => runs.filter((r) => r.completed).length, [runs]);
    const latestNight = useMemo(() => (runs.length > 0 ? runs[0].night : 'None'), [runs]);

    return (
        <div className="space-y-6" data-testid="bench-scoreboard">
            {/* Header controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        Nightly Benchmark Scoreboard
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Tracking daily readiness scores, step counts, and fact findings across benchmark cases.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <select
                        aria-label="Filter by case"
                        value={caseFilter}
                        onChange={(e) => setCaseFilter(e.target.value)}
                        className="bg-card border border-border text-foreground rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                        <option value="all">All cases</option>
                        {caseLabels.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>

                    <div className="flex rounded-md border border-border overflow-hidden">
                        {[7, 30, 90].map((d) => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={`px-3 py-1 text-xs font-medium transition-colors ${
                                    days === d
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-card text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={loadRuns}
                        disabled={loading}
                        className="p-1.5 rounded-md border border-border bg-card text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        title="Reload"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Metrics cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="text-xs font-medium text-muted-foreground">Total Runs ({days}d)</div>
                    <div className="text-2xl font-bold text-foreground mt-1">{runs.length}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {completedCount} completed ({runs.length ? Math.round((completedCount / runs.length) * 100) : 0}%)
                    </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="text-xs font-medium text-muted-foreground">Active Benchmark Cases</div>
                    <div className="text-2xl font-bold text-foreground mt-1">{caseLabels.length}</div>
                    <div className="text-xs text-muted-foreground mt-1">Cases in manifest</div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="text-xs font-medium text-muted-foreground">Latest Recorded Night</div>
                    <div className="text-2xl font-bold text-foreground mt-1">{latestNight}</div>
                    <div className="text-xs text-muted-foreground mt-1">UTC 01:00 schedule</div>
                </div>
            </div>

            {/* Runs table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center p-12 text-muted-foreground">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        Loading benchmark history...
                    </div>
                ) : filteredRuns.length === 0 ? (
                    <div className="text-center p-12 text-muted-foreground">
                        No benchmark runs found for the selected period.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-muted/50 text-xs text-muted-foreground uppercase border-b border-border">
                                <tr>
                                    <th className="px-4 py-3">Night</th>
                                    <th className="px-4 py-3">Case</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Readiness</th>
                                    <th className="px-4 py-3">Facts</th>
                                    <th className="px-4 py-3">Breakdown</th>
                                    <th className="px-4 py-3">Duration</th>
                                    <th className="px-4 py-3 text-right">Details</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredRuns.map((run) => {
                                    const diff = runDiffs[run.id];
                                    const isExpanded = expandedRunId === run.id;
                                    const facts = factsMap[run.id];
                                    const isLoadingFacts = loadingFacts[run.id];

                                    return (
                                        <React.Fragment key={run.id}>
                                            <tr
                                                className={`hover:bg-muted/20 transition-colors cursor-pointer ${
                                                    isExpanded ? 'bg-muted/30' : ''
                                                }`}
                                                onClick={() => toggleExpand(run.id)}
                                            >
                                                <td className="px-4 py-3 font-mono text-xs">{run.night}</td>
                                                <td className="px-4 py-3 font-medium text-foreground">
                                                    {run.case_label}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {run.status === 'completed' ? (
                                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-medium">
                                                            <CheckCircle2 className="w-3 h-3" />
                                                            Completed
                                                        </span>
                                                    ) : run.status === 'skipped' ? (
                                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 font-medium">
                                                            <Clock className="w-3 h-3" />
                                                            {run.error_class || 'Skipped'}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium">
                                                            <XCircle className="w-3 h-3" />
                                                            {run.error_class || run.status}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {run.readiness_score !== null && run.readiness_score !== undefined ? (
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-foreground">
                                                                {run.readiness_score}
                                                            </span>
                                                            {diff !== null && diff !== undefined && (
                                                                <span
                                                                    className={`inline-flex items-center text-xs font-semibold ${
                                                                        diff > 0
                                                                            ? 'text-emerald-500'
                                                                            : diff < 0
                                                                            ? 'text-destructive'
                                                                            : 'text-muted-foreground'
                                                                    }`}
                                                                >
                                                                    {diff > 0 ? (
                                                                        <TrendingUp className="w-3 h-3 mr-0.5" />
                                                                    ) : diff < 0 ? (
                                                                        <TrendingDown className="w-3 h-3 mr-0.5" />
                                                                    ) : (
                                                                        <Minus className="w-3 h-3 mr-0.5" />
                                                                    )}
                                                                    {diff > 0 ? `+${diff}` : diff}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted-foreground">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {run.facts_found !== null && run.facts_found !== undefined ? (
                                                        <span>
                                                            {run.facts_found} / {run.facts_total ?? '?'}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-xs text-muted-foreground">
                                                    {run.completed ? (
                                                        <div className="flex gap-2">
                                                            <span title="Partial">{run.partial || 0} P</span>
                                                            <span
                                                                title="Missing"
                                                                className={
                                                                    run.missing ? 'text-destructive font-semibold' : ''
                                                                }
                                                            >
                                                                {run.missing || 0} M
                                                            </span>
                                                            <span title="Not Exposed">{run.not_exposed || 0} NE</span>
                                                        </div>
                                                    ) : (
                                                        <span>—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-xs font-mono">
                                                    {run.seconds !== null && run.seconds !== undefined
                                                        ? `${Math.round(run.seconds)}s`
                                                        : '—'}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleExpand(run.id);
                                                        }}
                                                        className="text-muted-foreground hover:text-foreground p-1"
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronDown className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>

                                            {/* Expandable facts section */}
                                            {isExpanded && (
                                                <tr className="bg-muted/10">
                                                    <td colSpan={8} className="px-6 py-4">
                                                        <div className="space-y-3">
                                                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                                <span className="font-medium text-foreground">
                                                                    Run #{run.id} Details & Facts
                                                                </span>
                                                                <span className="font-mono">
                                                                    Image: {run.image_tag || 'unknown'} | Ref: {run.run_ref || 'none'}
                                                                </span>
                                                            </div>

                                                            {isLoadingFacts ? (
                                                                <div className="flex items-center py-4 text-xs text-muted-foreground">
                                                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                                    Loading facts...
                                                                </div>
                                                            ) : !facts || facts.length === 0 ? (
                                                                <div className="py-2 text-xs text-muted-foreground italic">
                                                                    No per-fact records stored for this run.
                                                                </div>
                                                            ) : (
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 pt-1">
                                                                    {facts.map((f) => {
                                                                        const isFound = f.status === 'FOUND';
                                                                        const isMissing = f.status === 'MISSING';
                                                                        const isPartial = f.status === 'PARTIAL';
                                                                        return (
                                                                            <div
                                                                                key={f.fact_id}
                                                                                className="flex items-center justify-between p-2 rounded border border-border bg-card text-xs"
                                                                            >
                                                                                <span
                                                                                    className="font-mono truncate max-w-[150px]"
                                                                                    title={f.fact_id}
                                                                                >
                                                                                    {f.fact_id}
                                                                                </span>
                                                                                <span
                                                                                    className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                                                                        isFound
                                                                                            ? 'bg-emerald-500/10 text-emerald-500'
                                                                                            : isMissing
                                                                                            ? 'bg-destructive/10 text-destructive'
                                                                                            : isPartial
                                                                                            ? 'bg-amber-500/10 text-amber-500'
                                                                                            : 'bg-muted text-muted-foreground'
                                                                                    }`}
                                                                                >
                                                                                    {f.status}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
